import { FunctionComponent, ReactNode, RefObject, useEffect, useRef } from 'react'
import { Location, useLocation } from 'react-router-dom'
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics'
import firebaseConfig from '../../firebase-config'
import Bugsnag, { NotifiableError } from '@bugsnag/js'

interface GAComponentProps {
    children: ReactNode
}

let firebaseInitialized: boolean = false

const isFirebaseAlreadyInitialized: (error: unknown) => boolean = (error: unknown): boolean => {
    if (error && typeof error === 'object' && 'message' in error) {
        const message: string = (error as { message: string }).message
        return message.includes('already exists') || message.includes('duplicate-app')
    }
    if (error && typeof error === 'object' && 'code' in error) {
        const code: string = (error as { code: string }).code
        return code === 'app/duplicate-app'
    }
    return false
}

const GAComponent: FunctionComponent<GAComponentProps> = ({ children }: GAComponentProps) => {
    const initializedRef: RefObject<boolean> = useRef<boolean>(false)
    const location: Location = useLocation()
    const pathname: string = location.pathname || '/'

    useEffect(() => {
        if (initializedRef.current || firebaseInitialized) return

        const initializeFirebase: () => Promise<void> = async (): Promise<void> => {
            try {
                await FirebaseAnalytics.initializeFirebase(firebaseConfig)
                await FirebaseAnalytics.setCollectionEnabled({ enabled: true })

                firebaseInitialized = true
                initializedRef.current = true
            } catch (err: unknown) {
                if (isFirebaseAlreadyInitialized(err)) {
                    firebaseInitialized = true
                    initializedRef.current = true

                    try {
                        await FirebaseAnalytics.setCollectionEnabled({ enabled: true })
                    } catch (error: unknown) {
                        console.error('Could not enable collection:', error)
                    }
                }
            }
        }

        initializeFirebase()
    }, [])

    useEffect(() => {
        if (!initializedRef.current) return

        const trackPageView: () => Promise<void> = async (): Promise<void> => {
            try {
                await FirebaseAnalytics.setScreenName({
                    screenName: pathname,
                    nameOverride: pathname
                })

                await FirebaseAnalytics.logEvent({
                    name: 'page_view',
                    params: {
                        page_location: window.location.href,
                        page_path: pathname,
                        page_title: document.title || pathname,
                        screen_name: pathname
                    }
                })

                await FirebaseAnalytics.logEvent({
                    name: 'screen_view',
                    params: {
                        screen_name: pathname,
                        screen_class: pathname
                    }
                })
            } catch (err) {
                Bugsnag.notify(err as NotifiableError)
            }
        }

        trackPageView()
    }, [pathname])

    return <>{children}</>
}

export default GAComponent
